<template>
    <!-- Tag Add/Edit Modal -->
    <TagAddEditModal
        v-show="isAddEditModalOpen"
        :isOpen="isAddEditModalOpen"
        :tag="selectedTag"
        @update:isOpen="
            (event) => {
                isAddEditModalOpen = event;
            }
        "
        @save="handleSave" />

    <div class="mx-auto w-full">
        <!-- Mobile Layout -->
        <div class="block md:hidden space-y-4 mb-6">
            <!-- Actions Row -->
            <div class="flex flex-col sm:flex-row gap-3">
                <Button
                    v-can-access.permissions="[$Permissions.WRITE_TAGS]"
                    variant="outline"
                    :disabled="loaderStore.isLoading('tags.fetch')"
                    class="gap-2 w-full sm:w-auto"
                    @click="openAddModal">
                    <PlusCircle class="h-4 w-4" />
                    Create
                </Button>
                
                <SearchInput
                    :loading="loaderStore.isLoading('tags.fetch')"
                    placeholder="Search by name, ID, type, or description"
                    class="w-full sm:flex-1"
                    @search="(input) => (searchQuery = input)" />
            </div>
            
            <!-- Filters Row -->
            <div class="flex gap-3 items-center">
                <MultiSelect
                    v-model="selectedFilterTypeValues"
                    :disabled="loaderStore.isLoading('tags.fetch')"
                    :options="TagTypeFilterOptions"
                    placeholder="Filter by tag type"
                    :option-label="'name'"
                    :track-by="'value'"
                    :show-tags="false"
                    class="w-full" />
            </div>
        </div>

        <!-- Desktop Layout -->
        <div class="hidden md:flex justify-between items-center mb-6">
            <div class="flex gap-3 items-center">
                <Button
                    v-can-access.permissions="[$Permissions.WRITE_TAGS]"
                    variant="outline"
                    :disabled="loaderStore.isLoading('tags.fetch')"
                    class="gap-2"
                    @click="openAddModal">
                    <PlusCircle class="h-4 w-4" />
                    Create
                </Button>
                
                <SearchInput
                    :loading="loaderStore.isLoading('tags.fetch')"
                    placeholder="Search by name, ID, type, or description"
                    @search="(input) => (searchQuery = input)" />
            </div>
            <div class="flex gap-3 items-center">
                <MultiSelect
                    v-model="selectedFilterTypeValues"
                    :disabled="loaderStore.isLoading('tags.fetch')"
                    :options="TagTypeFilterOptions"
                    placeholder="Filter by tag type"
                    :option-label="'name'"
                    :track-by="'value'"
                    :show-tags="false" />
            </div>
        </div>
        <div class="mt-2">
            <ScrollArea class="w-full" :class="(filteredTags?.length || 0) <= 10 ? 'h-auto' : 'h-81-dynamic'">
                <div class="flex flex-col gap-2 pr-4">
                    <!-- Skeleton cards when loading -->
                    <template v-if="loaderStore.isLoading('tags.fetch')">
                        <div
                            v-for="n in 5"
                            :key="`skeleton-${n}`"
                            class="flex items-center justify-between p-4 rounded-lg border bg-card text-card-foreground">
                            <div class="flex-1 space-y-2">
                                <!-- Tag Name and Status skeleton -->
                                <div class="flex items-center gap-2">
                                    <SkeletonLoader preset="table-name" :loading="true" />
                                    <SkeletonLoader preset="badge" :loading="true" />
                                </div>
                                <!-- Tag Types skeleton -->
                                <div class="flex flex-wrap gap-1">
                                    <SkeletonLoader preset="badge" :loading="true" />
                                    <SkeletonLoader preset="badge" :loading="true" />
                                </div>
                                <!-- Description skeleton -->
                                <SkeletonLoader preset="wide-field" :loading="true" />
                            </div>
                            <!-- Actions skeleton -->
                            <div class="flex items-center ml-4">
                                <SkeletonLoader preset="table-action" :loading="true" />
                            </div>
                        </div>
                    </template>
                    <!-- Actual data cards -->
                    <template v-else>
                        <div
                            v-for="tag in filteredTags"
                            :key="tag.id"
                            class="flex items-center justify-between p-4 rounded-lg border bg-card text-card-foreground hover:bg-accent/50 transition-colors">
                            <div class="flex-1 space-y-2">
                                <!-- Tag Name and Status -->
                                <div class="flex items-center gap-2">
                                    <h3 class="font-medium text-sm">{{ tag.name }}</h3>
                                    <Badge v-if="tag.system" variant="outline" class="border-primary text-primary"> System </Badge>
                                    <Badge v-else variant="default"> Custom </Badge>
                                </div>

                                <!-- Tag Types -->
                                <div class="flex flex-wrap gap-1">
                                    <Badge v-for="(type, index) in tag.type" :key="index" variant="outline">
                                        {{ TagTypeToDisplayName[type] }}
                                    </Badge>
                                </div>

                                <!-- Description -->
                                <div v-if="tag.description">
                                    <p class="text-sm text-muted-foreground">{{ tag.description }}</p>
                                </div>
                            </div>

                            <!-- Actions -->
                            <div class="flex items-center ml-4">
                                <div v-if="tag.system">
                                    <Badge variant="secondary"> Read-Only </Badge>
                                </div>
                                <div v-else>
                                    <LoaderCircle v-if="actionLoading(tag.id)" class="h-4 w-4 animate-spin" />
                                    <ActionMenu
                                        v-else
                                        v-can-access.permissions.or="[$Permissions.WRITE_TAGS, $Permissions.DELETE_TAGS]"
                                        variant="menu"
                                        :show-edit="userHasAccess([$Permissions.WRITE_TAGS], authStore.user)"
                                        :show-delete="userHasAccess([$Permissions.DELETE_TAGS], authStore.user)"
                                        :loading="actionLoading(tag.id)"
                                        @edit="openEditModal(tag)"
                                        @delete="handleDeleteTag(tag)" />
                                </div>
                            </div>
                        </div>

                        <!-- Empty State -->
                        <div v-if="!filteredTags?.length" class="flex flex-col items-center py-12">
                            <TableEmptyState />
                        </div>
                    </template>
                </div>
                <ScrollBar orientation="vertical" />
            </ScrollArea>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { MultiSelect } from '@/components/ui/multi-select';
import { PlusCircle, Search, LoaderCircle } from 'lucide-vue-next';
import { TagTypesInfo, TagTypeToDisplayName } from '@/utils/constants';
import { useDeleteConfirmation } from '@/composables/useDialog';
import TagAddEditModal from '@/components/Tags/TagAddEditModal.vue';
import { useTagsStore } from '@/stores/tags.store';
import { useAuthStore } from '@/stores/auth.store';
import { useLoaderStore } from '@/stores/loader.store.js';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import SearchInput from '@/components/global/SearchInput.vue';
import ActionMenu from '@/components/ActionMenu.vue';
import { Button } from '@/components/ui/button';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { userHasAccess } from '@/utils/utils.js';

const isAddEditModalOpen = ref(false);
const selectedTag = ref(null);
const selectedFilterTypes = ref([]);

const selectedFilterTypeValues = computed({
    get() {
        return selectedFilterTypes.value.map((type) => type.value);
    },
    set(values) {
        selectedFilterTypes.value = values.map((value) => TagTypeFilterOptions.value.find((option) => option.value === value)).filter(Boolean);
    }
});

const tagsStore = useTagsStore();
const authStore = useAuthStore();
const loaderStore = useLoaderStore();
const searchQuery = ref('');

onMounted(async () => {
    await tagsStore.fetch();
});

const actionLoading = computed(() => (tagId) => loaderStore.isLoading(`tags.delete.${tagId}` || loaderStore.isLoading(`tags.update.${tagId}`)));

const TagTypeFilterOptions = computed(() => TagTypesInfo.filter((type) => tagsStore.all.some((tag) => tag.type.includes(type.value))));

const filteredTags = computed(() => {
    if (!selectedFilterTypes.value.length && !searchQuery.value) {
        return tagsStore.all;
    }

    return tagsStore.all.filter((tag) => {
        const matchesType = selectedFilterTypes.value.length ? selectedFilterTypes.value.some((type) => tag.type.includes(type.value)) : true;

        const matchesSearch = searchQuery.value
            ? tag.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
              tag.id.toString().includes(searchQuery.value) ||
              tag.description.toLowerCase().includes(searchQuery.value.toLowerCase())
            : true;

        return matchesType && matchesSearch;
    });
});

const openAddModal = () => {
    selectedTag.value = null;

    isAddEditModalOpen.value = true;
};

const openEditModal = (tag) => {
    selectedTag.value = tag;

    isAddEditModalOpen.value = true;
};

const handleSave = async (tagToSave) => {
    try {
        if (tagToSave.id) {
            // Update via API call in the store
            await tagsStore.updateTag(tagToSave);
        } else {
            // Add new tag via API call in the store
            await tagsStore.addTag(tagToSave);
        }

        isAddEditModalOpen.value = false;
        selectedTag.value = null;
    } catch (error) {
        console.error('Error saving/editing tag: ', error);
    }
};

const handleDeleteTag = async (tag) => {
    try {
        await useDeleteConfirmation({
            entity: 'tag',
            name: tag.name,
            title: 'Delete Tag',
            message: `Are you sure you want to delete the tag <strong>"${tag.name}"</strong>? This action cannot be undone.`,
            onConfirm: async () => {
                await tagsStore.deleteTag(tag.id);
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Delete failed:', error);
        }
    }
};
</script>

<style scoped>
</style>
